import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { LocalizationContext } from '../../contexts';
import UpdateController from '../UpdateController';
import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-react-layout';
import Uploader, { uploaderTypes } from '../Uploader';
import { useRepositoryHook } from '../../hooks';
import './index.scss';
import VideoPreview from '../VideoPreview';
import PublishWorkspacePanel from '../PublishWorkspacePanel';
import { isValidMaxRows, isValidUrl } from './validation';
import {
    isArrayEmpty, isArrayNotEmpty, isArraysNotEmpty,
    getValueFromJson,
    getFilteredArrayByOneParam,
    removeOrphanChildren,
    removeSeparator, addPrefixForItems, addPrefixForSkills, addPrefixForSyllabusSubjects,
    removeChildren_syllabusSkills
} from './array-utils';
import {
    NO_INSERT_MODE,
    DIMENSIONS_LANG,
    TITLE_MAX_CHARS,
    SUBTITLE_MAX_CHARS,
    SUMMARY_MAX_CHARS,
    SUMMARY_MAX_CHARS_BOOK,
    URL_MAX_CHARS,
    COURSE_CONTENT_MAX_CHARS,
    COURSE_CONTENT_MAX_ROWS,
    DISCIPLINES_MAX,
    SYLLABUS_MAX,
    SKILLS_MAX,
    SYLLABUS_SUBJECT_ID,
    SYLLABUS_SUBSUBJECT_ID,
    SKILLS_SUBJECT_ID,
    SKILLS_SUBSUBJECT_ID
} from './consts';

import {
    /*Input, MaskedTextBox, NumericTextBox,
    Checkbox, ColorPicker, Switch, */
    RadioGroup
    // ,    Slider, SliderLabel, TextArea
} from '@progress/kendo-react-inputs';
import { FormInput, FormCheckbox, FormTextArea, FormDropDownList, FormSwitch, FormMultiSelect, FormNumericTextBox } from './form-components.jsx';

export default function RepositoryItemEditor({ editItem, editItemDimensions, dimensions, dependencies, backLink, ...rest }) {
    const { getSectorsByDisciplines } = useRepositoryHook();

    const [localization] = useContext(LocalizationContext);
    const history = useHistory();

    const [item, _setItem] = useState({});
    const setItem = (newItemData) => {
        _setItem({ ...item, ...newItemData });
    };

    const [maxSyllabusSubjects, setMaxSyllabusSubjects] = useState(SYLLABUS_MAX);
    const [maxSkillsSubjects, setMaxSkillsSubjects] = useState(SKILLS_MAX);
    const [maxGeneralSkillsSubjects, setMaxGeneralSkillsSubjects] = useState(SKILLS_MAX);
    const [disciplinesSectors, setDisciplinesSectors] = useState(null);
    const [sectorsByDis, setSectorsByDis] = useState(null);

    const [newThumbnail, setNewThumbnail] = useState(false);

    const [itemDimensions, setItemDimensions] = useState({});

    const [isMultiDisciplines, setIsMultiDisciplines] = useState(false);

    const [updateData, _setUpdateData] = useState({ hasChanges: false });
    const setUpdateData = (newUpdateData) => {
        _setUpdateData({ ...updateData, ...newUpdateData });
    };

    const [isValidForm, setIsValidForm] = useState(false);

    const sortingByName = (a, b) => (a[DIMENSIONS_LANG] > b[DIMENSIONS_LANG]) ? 1 : -1;

    const activityTypes = Object.values(dimensions.activityTypes).sort(sortingByName);
    const ageGrades = Object.values(dimensions.ageGrades).sort(sortingByName);
    const audiences = Object.values(dimensions.audiences).sort(sortingByName);
    const authors = Object.values(dimensions.authors).sort(sortingByName);
    const copyrightHolders = Object.values(dimensions.copyrightHolders).sort(sortingByName);
    const disciplines = Object.values(dimensions.disciplines).sort(sortingByName);
    const itemContext = Object.values(dimensions.itemContext).sort(sortingByName);
    const itemSource = Object.values(dimensions.itemSource).sort(sortingByName);
    const itemTypes = Object.values(dimensions.itemTypes).sort(sortingByName);
    const pedagogicValues = Object.values(dimensions.pedagogicValues).sort(sortingByName);
    const publishers = Object.values(dimensions.publishers).sort(sortingByName);
    const sectors = Object.values(dimensions.sectors).sort(sortingByName);
    const subActivityTypes = Object.values(dimensions.subActivityTypes).sort(sortingByName);
    const workspaces = dimensions.workspaces ? Object.values(dimensions.workspaces).sort(sortingByName) : [];

    const syllabusDetailsArray = Object.values(dimensions.syllabusDetails).sort(sortingByName);
    const syllabusSubjectsArray = Object.values(dimensions.syllabusSubjects).sort(sortingByName);
    const syllabusSubsubjectsArray = Object.values(dimensions.syllabusSubsubjects).sort(sortingByName);

    const skillsArray = Object.values(dimensions.skills).sort(sortingByName);
    const skillsDetailsArray = Object.values(dimensions.skillsDetails).sort(sortingByName);
    const subskillsArray = Object.values(dimensions.subskills).sort(sortingByName);

    const generalSkillsArray = Object.values(dimensions.generalSkills).sort(sortingByName);
    const generalSkillsDetailsArray = Object.values(dimensions.generalSkillsDetails).sort(sortingByName);
    const generalSubskillsArray = Object.values(dimensions.generalSubskills).sort(sortingByName);

    const yearCycles = Object.values(dimensions.yearCycles).sort(sortingByName);;
    const yearCycleCategories = Object.values(dimensions.yearCycleCategories).sort(sortingByName);

    const syllabusAgeGrades = Object.values(dependencies.syllabusSubjects.ageGrades).sort(sortingByName);
    const syllabusDisciplines = Object.values(dependencies.syllabusSubjects.disciplines).sort(sortingByName);

    const skillsDisciplines = Object.values(dependencies.skills.disciplines).sort(sortingByName);
    const skillsAgeGrades = Object.values(dependencies.skills.ageGrades).sort(sortingByName);
    const generalSkillsAgeGrades = Object.values(dependencies.generalSkills.ageGrades).sort(sortingByName);

    const buildDurationArray = () => {
        let durationArray = [];
        for (let i = 0; i < 5; i++) {
            let minutes = ((i + 1) * 10).toString();
            durationArray[i] = { 'value': minutes, 'displayValue': minutes + ' ' + localization.labels.repositoryManagement.minutes }
        }
        return durationArray;
    }

    let durationData = [...buildDurationArray(),
    { 'value': '60', 'displayValue': localization.labels.repositoryManagement.duration.minutes60 },
    { 'value': '90', 'displayValue': localization.labels.repositoryManagement.duration.minutes90 },
    { 'value': '120', 'displayValue': localization.labels.repositoryManagement.duration.minutes120 },
    { 'value': '180', 'displayValue': localization.labels.repositoryManagement.duration.minutes180 }
    ]

    const buildDisciplinesSectors = async () => {
        await getSectorsByDisciplines().then((result) => {
            setDisciplinesSectors(result);
        });
    }

    const [syllabusDataForGrid, setSyllabusDataForGrid] = useState([]);
    const [skillsDataForGrid, setSkillsDataForGrid] = useState([]);
    const [generalSkillsDataForGrid, setGeneralSkillsDataForGrid] = useState([]);

    useEffect(() => { //run once - init all prefixes for Syllabus&Skills
        if (syllabusSubjectsArray[0].isPrefixAdded) return;
        syllabusSubjectsArray[0].isPrefixAdded = true;

        addPrefixForSyllabusSubjects(syllabusSubjectsArray, syllabusDisciplines, syllabusAgeGrades, dimensions, localization);
        addPrefixForItems(syllabusSubsubjectsArray, dimensions.syllabusSubsubjects, dimensions.syllabusSubjects, SYLLABUS_SUBJECT_ID, 'SyllabusSubjects');
        addPrefixForItems(syllabusDetailsArray, dimensions.syllabusDetails, dimensions.syllabusSubsubjects, SYLLABUS_SUBSUBJECT_ID, 'SyllabusSubSubjects');

        addPrefixForSkills(skillsArray, skillsDisciplines, skillsAgeGrades, dimensions, localization);
        addPrefixForItems(subskillsArray, dimensions.subskills, dimensions.skills, SKILLS_SUBJECT_ID, 'Skills');
        addPrefixForItems(skillsDetailsArray, dimensions.skillsDetails, dimensions.subskills, SKILLS_SUBSUBJECT_ID, 'SubSkills');

        addPrefixForItems(generalSubskillsArray, dimensions.generalSubskills, dimensions.generalSkills, SKILLS_SUBJECT_ID, 'GeneralSkills');
        addPrefixForItems(generalSkillsDetailsArray, dimensions.generalSkillsDetails, dimensions.generalSubskills, SKILLS_SUBSUBJECT_ID, 'GeneralSubSkills');

        buildDisciplinesSectors();
    }, []);

    const isItemCourse = () => {
        return itemDimensions.itemTypes?.indexOf('course') >= 0;
    }
    const isItemBook = () => {
        return itemDimensions.itemTypes?.indexOf('book') >= 0;
    }
    const isItemBookOrCourse = () => {
        return isItemCourse() || isItemBook();
    }
    const isItemPedagogy = () => {
        return item.activityType === 'nanoCourse' || item.activityType === 'selTime';
    }
    const showItemSubActivityType = () => {
        return item.activityType !== '_other';
    }
    const isItemPedagogyNonSkills = () => {
        return isItemPedagogy() && (item.subActivityType !== 'skills');
    }

    const [isCourse, setIsCourse] = useState(false);
    const [isBook, setIsBook] = useState(false);
    const [isBookOrCourse, setIsBookOrCourse] = useState(false);
    const [isPedagogy, setIsPedagogy] = useState(false);
    const [isPedagogyNonSkills, setIsPedagogyNonSkills] = useState(false);
    const [showSubActivityType, setShowSubActivityType] = useState(false);

    useEffect(() => {
        if (isItemBook()) {
            item.duration = 0;
        }

        // if (itemDimensions.itemTypes?.indexOf('teacherRef-nano-course') >= 0) {
        //     item.activityType = 'nanoCourse';
        // }

        if (isItemPedagogy()) {
            itemDimensions.audiences = ['teachers'];
        } else {
            if (item.activityType === '_other') {
                item.subActivityType = null;
            }
        }

        setIsCourse(isItemCourse());
        setIsBook(isItemBook());
        setIsBookOrCourse(isItemBookOrCourse());
        setIsPedagogy(isItemPedagogy());
        setIsPedagogyNonSkills(isItemPedagogyNonSkills());
        setShowSubActivityType(showItemSubActivityType());
    }, [itemDimensions.itemTypes, item.activityType, item.subActivityType]);

    useEffect(() => {
        setItem({ ...editItem });
    }, [editItem]);

    useEffect(() => {
        setItemDimensions({ ...editItemDimensions });
    }, [editItemDimensions]);

    useEffect(() => {
        let isMultiDisciplines = itemDimensions.disciplines && itemDimensions.disciplines.length > 1;
        setIsMultiDisciplines(isMultiDisciplines);
    }, [itemDimensions.disciplines]);

    const [yearCyclesFiltered, setYearCyclesFiltered] = useState([]);

    useEffect(() => {
        let filteredArray = [];
        if (isArrayNotEmpty(itemDimensions.yearCycleCategories)) {
            filteredArray = (yearCycles.filter(x => itemDimensions.yearCycleCategories.indexOf(x.parentId) >= 0));
        }
        setYearCyclesFiltered(filteredArray);

        itemDimensions.yearCycles = removeOrphanChildren(itemDimensions.yearCycles, itemDimensions.yearCycleCategories, dimensions.yearCycles, 'parentId');

    }, [itemDimensions.yearCycleCategories]);

    const getYearCycleCategoriesValuesByChildren = () => {
        if (isArrayEmpty(itemDimensions.yearCycles)) return [];

        if (!itemDimensions.yearCycleCategories) {
            itemDimensions.yearCycleCategories = [];
        }

        itemDimensions.yearCycles.forEach(yearCycle => {
            let parent = dimensions.yearCycles[yearCycle].parentId;
            if (parent && itemDimensions.yearCycleCategories.indexOf(parent) < 0) {
                itemDimensions.yearCycleCategories.push(parent);
            }
        });

        setItemDimensions({ ...itemDimensions });
    }

    useEffect(() => {
        getYearCycleCategoriesValuesByChildren();
    }, [itemDimensions.yearCycles]);

    useEffect(() => {
        itemDimensions.syllabusSubjects = removeChildren_syllabusSkills(itemDimensions.syllabusSubjects, syllabusDisciplines, syllabusAgeGrades, itemDimensions);
        itemDimensions.skills = removeChildren_syllabusSkills(itemDimensions.skills, skillsDisciplines, skillsAgeGrades, itemDimensions);
        itemDimensions.generalSkills = removeChildren_syllabusSkills(itemDimensions.generalSkills, null, generalSkillsAgeGrades, itemDimensions);

        if ((itemDimensions.ageGrades?.length === 0) || (itemDimensions.disciplines?.length === 0)) {
            setData_SyllabusSubjects([]);
            setData_SyllabusSubsubjects([]);
            setData_syllabusDetails([]);

            setData_Skills([]);
            setData_Subskills([]);
            setData_SkillsDetails([]);

            setData_GeneralSkills([]);
            setData_GeneralSubskills([]);
            setData_GeneralSkillsDetails([]);
        }
    }, [itemDimensions.ageGrades, itemDimensions.disciplines]);

    useEffect(() => {
        itemDimensions.syllabusSubsubjects = removeOrphanChildren(itemDimensions.syllabusSubsubjects, itemDimensions.syllabusSubjects, dimensions.syllabusSubsubjects, SYLLABUS_SUBJECT_ID);

        if (itemDimensions.syllabusSubjects?.length === 0) {
            setData_SyllabusSubsubjects([]);
            setData_syllabusDetails([]);
        }
    }, [itemDimensions.syllabusSubjects]);

    useEffect(() => {
        itemDimensions.syllabusDetails = removeOrphanChildren(itemDimensions.syllabusDetails, itemDimensions.syllabusSubsubjects, dimensions.syllabusDetails, SYLLABUS_SUBSUBJECT_ID);
    }, [itemDimensions.syllabusSubsubjects]);

    useEffect(() => {
        itemDimensions.subskills = removeOrphanChildren(itemDimensions.subskills, itemDimensions.skills, dimensions.subskills, SKILLS_SUBJECT_ID);

        if (itemDimensions.skills?.length === 0) {
            setData_Subskills([]);
            setData_SkillsDetails([]);
        }
    }, [itemDimensions.skills]);

    useEffect(() => {
        itemDimensions.skillsDetails = removeOrphanChildren(itemDimensions.skillsDetails, itemDimensions.subskills, dimensions.skillsDetails, SKILLS_SUBSUBJECT_ID);
    }, [itemDimensions.subskills]);

    useEffect(() => {
        itemDimensions.generalSubskills = removeOrphanChildren(itemDimensions.generalSubskills, itemDimensions.generalSkills, dimensions.generalSubskills, SKILLS_SUBJECT_ID);

        if (itemDimensions.generalSkills?.length === 0) {
            setData_GeneralSubskills([]);
            setData_GeneralSkillsDetails([]);
        }
    }, [itemDimensions.generalSkills]);

    useEffect(() => {
        itemDimensions.generalSkillsDetails = removeOrphanChildren(itemDimensions.generalSkillsDetails, itemDimensions.generalSubskills, dimensions.generalSkillsDetails, SKILLS_SUBSUBJECT_ID);
    }, [itemDimensions.generalSubskills]);

    useEffect(() => {
        if (!isPedagogy && !item.isAllDisciplines && (itemDimensions?.sectors?.length > 0)) {
            filterSectors(getValueForMultiSelect(itemDimensions.sectors, dimensions.sectors));
        }
    }, [itemDimensions.sectors, dimensions.sectors, isPedagogy, item.isAllDisciplines]);

    const getTextFromItemDimension = (array, key) => {
        let filteredArray = array.filter((value) => { return (value.id === key); });
        if (!filteredArray || filteredArray.length === 0) return;
        let text = filteredArray[0][DIMENSIONS_LANG]
        return text;
    };

    const updateItem = () => {
        const promise = rest.update({ ...item }, { ...itemDimensions }, newThumbnail).then(() => {
            setNewThumbnail(false);
            setUpdateData({
                hasChanges: false
            });
        });
        return promise;
    };

    const formatDate = (inputDate) => {
        let date = '';
        if (inputDate) {
            date = new Date(inputDate);
            let year = date.getFullYear();
            let month = date.getMonth() + 1;
            month = month < 10 ? '0' + month : month;
            let day = date.getDate();
            let hours = date.getHours();
            let minutes = date.getMinutes();
            minutes = minutes < 10 ? '0' + minutes : minutes;
            date = day + '/' + month + '/' + year + ' ' + hours + ':' + minutes;
        }
        return date;
    }

    // const deleteItem = () => {
    /* TODO */
    // };

    const backToGrid = () => {
        history.push(backLink);
    };

    const topTools = <div>
        <Button className="update_controller-update_btn" onClick={backToGrid}>{localization.labels.repositoryManagement.back_to_item_list}</Button>
        {/* {item.id && <Button className="update_controller-update_btn" onClick={deleteItem}>{localization.labels.repositoryManagement.delete_item}</Button>} */}
    </div>;

    const handleSubmit = (dataItem) => console.log(JSON.stringify(dataItem, null, 2));

    const getValueForDropDownList = (itemDimensionArray, dimensionArray) => {
        return isArrayNotEmpty(itemDimensionArray) ? dimensionArray[itemDimensionArray[0]] : '';
    }

    const getValueForMultiSelect = (itemDimensionArray, dimensionArray) => {
        return itemDimensionArray ? itemDimensionArray.map(i => { return dimensionArray[i]; }) : [];
    }

    const onFieldChange = (key, val) => {
        var newData = {};
        newData[key] = val;
        setItem(newData);
        setUpdateData({ hasChanges: true });
    }

    const onInformaticApprovedFieldChange = (key, val) => {
        var newData = {};
        newData[key] = val;
        newData['informaticApprovalDate'] = (val) ? new Date().toISOString().slice(0, 19).replace('T', ' ') : '0001-01-01T00:00:00';
        setItem(newData);
        setUpdateData({ hasChanges: true });
    }

    const onAdditionalInfoFieldChange = (parentKey, key, val) => {
        var newData = { ...item };
        if (!newData['additionalInfo'][parentKey]) {
            newData['additionalInfo'][parentKey] = {};
        }
        newData['additionalInfo'][parentKey][key] = val;
        setItem(newData);
        setUpdateData({ hasChanges: true });
    }

    const onDropDownListFieldChange = (key, val) => {
        let new_itemDimensions = { ...itemDimensions };
        new_itemDimensions[key] = val ? [val.id] : [];
        setItemDimensions(new_itemDimensions);
        setUpdateData({ hasChanges: true });
    }

    const onMultiSelectFieldChange = (key, val, maxItems = null) => {
        if ((maxItems != null) && (val.length > maxItems))
            return;

        //remove duplicates values (same id)
        // if (val.length > 1) {
        //     let uniqIds = {};
        //     val = val.filter(obj => !uniqIds[obj.id] && (uniqIds[obj.id] = true)); 
        // }

        let new_itemDimensions = { ...itemDimensions };
        new_itemDimensions[key] = val ? val.map(i => { return i.id; }) : [];
        setItemDimensions(new_itemDimensions);
        setUpdateData({ hasChanges: true });
    }

    const getDurationValue = (val) => {
        if (isItemCourse()) {
            return val;
        }

        // eslint-disable-next-line eqeqeq
        let displayVal = durationData.find((x) => x.value == val);
        displayVal = displayVal ? displayVal.displayValue : '';
        return Number.isInteger(val * 1) && val > 0 ? { 'value': val, 'displayValue': displayVal } : val;
    }

    const getActivityTypeValue = (val, dimension) => {
        return (typeof val === "string") ? dimension[val] : val;
    }

    const getHintForPublish = () => {
        let hint;
        if (isValidForm) {
            hint = item.isPublished ?
                localization.labels.repositoryManagement.hints.isPublishTrue : localization.labels.repositoryManagement.hints.readyForPublish;
        }
        else {
            hint = (item.isPublished ?
                localization.labels.repositoryManagement.hints.publishedButNotValid : localization.labels.repositoryManagement.hints.publishNotReady)
                + ' ' + getValidationMessageForSyllabusSubjects();
        }

        return hint;
    }

    const getValidationMessageForSyllabus = (length, maxLimit, msgLimit) => {
        let msg = '';
        if (!isBookOrCourse && !isPedagogy && (length > maxLimit)) {
            msg = msgLimit;
        }
        return msg;
    }

    const getValidationMessageForSkills = (length, maxLimit, msgLimit) => {
        let msg = '';
        if (!isBookOrCourse && !isPedagogyNonSkills && (length > maxLimit)) {
            msg = msgLimit;
        }
        return msg;
    }

    const getValidationMessageForSyllabusSubjects = () => {
        let msg = '';

        if (isItemPedagogy()) return '';

        function getGrades(subject) {
            return subject.substring(subject.indexOf('-') + 2, subject.lastIndexOf(':'))
                .replace(localization.labels.repositoryManagement.grade, '')
                .replace(localization.labels.repositoryManagement.grades, '');
        }

        if (isArraysNotEmpty([itemDimensions.disciplines, itemDimensions.ageGrades])) {
            if (itemDimensions.disciplines.length === 1) {
                itemDimensions.disciplines.forEach(discipline => {
                    let disciplineName = disciplines.find(d => d.id === discipline)[DIMENSIONS_LANG];
                    itemDimensions.ageGrades.forEach(ageGrade => {
                        let ageGradeName = ageGrades.find(a => a.id === ageGrade)[DIMENSIONS_LANG];
                        let currentAgeGrade = ageGradeName[ageGradeName.length - 1];
                        let isFound = syllabusDataForGrid.find(d => (d.subject.includes(disciplineName) && getGrades(d.subject).includes(currentAgeGrade)));
                        if (!isFound
                            && (ageGradeName !== ageGrades[0][DIMENSIONS_LANG])) { // grade0 (kindergarten) don't need disciplines
                            msg += `${disciplineName} - ${ageGradeName}, `;
                        }
                    });
                });
            } else { //itemDimensions.disciplines.length > 1
                itemDimensions.disciplines.forEach(discipline => {
                    let disciplineName = disciplines.find(d => d.id === discipline)[DIMENSIONS_LANG];
                    let isFound = syllabusDataForGrid.find(d => d.subject.includes(disciplineName));
                    if (!isFound) {
                        msg += `${disciplineName}, `;
                    }
                });
                itemDimensions.ageGrades.forEach(ageGrade => {
                    let ageGradeName = ageGrades.find(a => a.id === ageGrade)[DIMENSIONS_LANG];
                    let currentAgeGrade = ageGradeName[ageGradeName.length - 1];
                    let isFound = syllabusDataForGrid.find(d => getGrades(d.subject).includes(currentAgeGrade));
                    if (!isFound) {
                        msg += `${ageGradeName}, `;
                    }
                });
            }
        }
        if (msg !== '') {
            msg = localization.labels.repositoryManagement.validators.missingSyllabusSubjects + ': ' + msg;
            msg = msg.slice(0, -2);
        }
        return msg;
    }

    const isAllowMaxForBookOrCourse = (event) => {
        return isBookOrCourse || event.nativeEvent.target.classList.contains('k-i-close');
    }

    const updateSectorsByDisciplines = (value) => {
        const sectors = [];
        if (value) {
            value.forEach(row => {
                var res = disciplinesSectors[row.id];
                sectors.push(res);
            });

            setSectorsByDis(sectors[0]);
        }
    }

    const filterSectors = (val) => {
        const sectors = [];

        val.forEach(row => {
            sectors.push(row.id);
        });
        setSectorsByDis(sectors);
    }

    const disciplinesChoice = [
        { label: localization.labels.repositoryManagement.singleDiscipline, value: 'singleDiscipline' },
        { label: localization.labels.repositoryManagement.multiDiscipline, value: 'multiDiscipline' },
        { label: localization.labels.repositoryManagement.allDisciplines, value: 'allDisciplines' },
    ];

    const getDisciplinesValue = () => {
        if (isPedagogy && item.isAllDisciplines) {
            return 'allDisciplines';
        }
        else {
            return isMultiDisciplines ? 'multiDiscipline' : 'singleDiscipline'
        }
    }

    const getAudiencesHint = (index) => {
        return localization.labels.repositoryManagement.hints.audiences.hints[index];
    }

    const getAudiencesTitle = (index) => {
        return localization.labels.repositoryManagement.hints.audiences.titles[index];
    }

    const getAudiencesRow = (index) => {
        return <div><b>{getAudiencesTitle(index)} - </b> {getAudiencesHint(index)}</div>
    }

    const [syllabusSubjects, setData_SyllabusSubjects] = useState([]);
    const [syllabusSubsubjects, setData_SyllabusSubsubjects] = useState([]);
    const [syllabusDetails, setData_syllabusDetails] = useState([]);

    const [skills, setData_Skills] = useState([]);
    const [subskills, setData_Subskills] = useState([]);
    const [skillsDetails, setData_SkillsDetails] = useState([]);

    const [generalSkills, setData_GeneralSkills] = useState([]);
    const [generalSubskills, setData_GeneralSubskills] = useState([]);
    const [generalSkillsDetails, setData_GeneralSkillsDetails] = useState([]);

    const getFilteredArrayByGradesAndDisciplines = (ageGradesArray, disciplinesArray, arrayToFilter) => {
        let filteredArray = [];

        if (isArrayNotEmpty(itemDimensions.ageGrades)) {
            let filteredGradesArray = ageGradesArray.filter(x => itemDimensions.ageGrades.indexOf(x.value) >= 0);
            let filteredGradesKeys = [];
            for (let i = 0; i < filteredGradesArray.length; i++) {
                filteredGradesKeys.push(filteredGradesArray[i]['key']);
            }

            if (disciplinesArray && isArrayNotEmpty(itemDimensions.disciplines)) {
                let filteredDisciplinesArray = disciplinesArray.filter(x => itemDimensions.disciplines.indexOf(x.value) >= 0);
                let filteredDisciplinesKeys = [];
                for (let i = 0; i < filteredDisciplinesArray.length; i++) {
                    filteredDisciplinesKeys.push(filteredDisciplinesArray[i]['key']);
                }

                filteredArray = arrayToFilter.filter(x =>
                    (filteredGradesKeys.indexOf(x.id) >= 0) && (filteredDisciplinesKeys.indexOf(x.id) >= 0));
            }
            else {
                filteredArray = arrayToFilter.filter(x =>
                    (filteredGradesKeys.indexOf(x.id) >= 0));
            }
        }

        return filteredArray;
    }

    const calcLimits = () => {
        if (isArraysNotEmpty([itemDimensions.ageGrades, itemDimensions.disciplines])) {
            let disciplinesPerGrades = itemDimensions.ageGrades.length * itemDimensions.disciplines.length;
            setMaxSyllabusSubjects(disciplinesPerGrades * SYLLABUS_MAX);
            setMaxSkillsSubjects(SKILLS_MAX - generalSkillsDataForGrid?.length);
            setMaxGeneralSkillsSubjects(SKILLS_MAX - skillsDataForGrid?.length);
        }
    }

    const getMsgSyllabusLimit = () => {
        calcLimits();
        return `${localization.labels.repositoryManagement.item.limit} 
                ${maxSyllabusSubjects} 
                ${localization.labels.repositoryManagement.item.syllabusLimit}`;
    }

    const getMsgSkillsLimit = (general = false) => {
        calcLimits();
        return `${localization.labels.repositoryManagement.item.limit} 
                ${general ? maxGeneralSkillsSubjects : maxSkillsSubjects} 
                ${localization.labels.repositoryManagement.item.skillsLimit}`;
    }

    useEffect(() => {
        calcLimits();
    }, [itemDimensions.ageGrades, itemDimensions.disciplines]);

    useEffect(() => {
        setData_SyllabusSubjects(getFilteredArrayByGradesAndDisciplines(syllabusAgeGrades, syllabusDisciplines, syllabusSubjectsArray));
        setData_SyllabusSubsubjects(getFilteredArrayByOneParam(itemDimensions.syllabusSubjects, syllabusSubsubjectsArray, SYLLABUS_SUBJECT_ID));
        setData_syllabusDetails(getFilteredArrayByOneParam(itemDimensions.syllabusSubsubjects, syllabusDetailsArray, SYLLABUS_SUBSUBJECT_ID));
    }, [itemDimensions.ageGrades, itemDimensions.disciplines, itemDimensions.syllabusSubjects, itemDimensions.syllabusSubsubjects]);

    useEffect(() => {
        setData_Skills(getFilteredArrayByGradesAndDisciplines(skillsAgeGrades, skillsDisciplines, skillsArray));
        setData_Subskills(getFilteredArrayByOneParam(itemDimensions.skills, subskillsArray, SKILLS_SUBJECT_ID));
        setData_SkillsDetails(getFilteredArrayByOneParam(itemDimensions.subskills, skillsDetailsArray, SKILLS_SUBSUBJECT_ID));
    }, [itemDimensions.ageGrades, itemDimensions.disciplines, itemDimensions.skills, itemDimensions.subskills]);

    useEffect(() => {
        setData_GeneralSkills(getFilteredArrayByGradesAndDisciplines(generalSkillsAgeGrades, null, generalSkillsArray));
        setData_GeneralSubskills(getFilteredArrayByOneParam(itemDimensions.generalSkills, generalSubskillsArray, SKILLS_SUBJECT_ID));
        setData_GeneralSkillsDetails(getFilteredArrayByOneParam(itemDimensions.generalSubskills, generalSkillsDetailsArray, SKILLS_SUBSUBJECT_ID));
    }, [itemDimensions.ageGrades, itemDimensions.disciplines, itemDimensions.generalSkills, itemDimensions.generalSubskills]);

    useEffect(() => {
        if (!isPedagogy) {
            item.isAllDisciplines = false;
        }
    }, [isPedagogy, item.itemTypes]);

    const isValidFormCheck = useCallback(() => {
        if (!item || !itemDimensions)
            return false;

        // Check if workspace is ivritIL - skip certain mandatory fields for this workspace
        const isIvritIL = itemDimensions.workspaces?.some(ws => ws.toLowerCase() === 'ivritil');

        let validationMessageForSkills =
            getValidationMessageForSkills(skillsDataForGrid.length, maxSkillsSubjects, getMsgSkillsLimit())
            + getValidationMessageForSkills(generalSkillsDataForGrid.length, maxGeneralSkillsSubjects, getMsgSkillsLimit(true));

        let checkForDuration = isIvritIL || isItemBookOrCourse() ? true : (item.duration > 0);

        let checkForSkills = isIvritIL || (isItemBookOrCourse() || isItemPedagogyNonSkills()) ? true
            : (validationMessageForSkills === '' && (isArrayNotEmpty(itemDimensions.skills) || isArrayNotEmpty(itemDimensions.generalSkills)));

        let checkForSyllabus = isIvritIL || (isItemBookOrCourse() || isItemPedagogy() || item.isAllDisciplines) ? true
            : (isArrayNotEmpty(itemDimensions.syllabusSubjects)
                && getValidationMessageForSyllabus(syllabusDataForGrid.length, maxSyllabusSubjects, getMsgSyllabusLimit()) === '')
            && getValidationMessageForSyllabusSubjects() === '';

        let validCourse = isCourse ? (
            isValidUrl(getValueFromJson(item.additionalInfo, 'courseData', 'courseMarketingURL')) &&
            isValidUrl(getValueFromJson(item.additionalInfo, 'courseData', 'courseVideoURL')) &&
            isValidMaxRows(COURSE_CONTENT_MAX_ROWS, getValueFromJson(item.additionalInfo, 'courseData', 'courseContent')))
            : true;

        // For ivritIL workspace, ageGrades is not required
        let requiredDimensionArrays = [itemDimensions.itemTypes, itemDimensions.audiences, itemDimensions.sectors];
        if (!isIvritIL) {
            requiredDimensionArrays.push(itemDimensions.ageGrades);
        }

        let isValidForm =
            checkForDuration
            && checkForSkills
            && checkForSyllabus
            && validCourse
            && item.title
            && item.summary
            && item.thumbnailId
            && (item.isAllDisciplines || isArrayNotEmpty(itemDimensions.disciplines))
            && isArraysNotEmpty(requiredDimensionArrays);

        if (!isValidForm && updateData.hasChanges) {
            item.isPublished = false;
        }
        return isValidForm;
    }, [item, itemDimensions,
        maxSkillsSubjects, maxSyllabusSubjects,
        syllabusDataForGrid.length, skillsDataForGrid.length, generalSkillsDataForGrid.length]);

    useEffect(() => {
        setIsValidForm(isValidFormCheck());
    }, [isValidFormCheck, item, itemDimensions]);


    const getDetailItem = (subjectsArray, subSubjecsArray, detailsArray, detailItem, subjectId, subSubjectId) => {
        let subSubject = detailsArray.find(x => x.id === detailItem)[subSubjectId];
        let subject = subSubjecsArray.find(x => x.id === subSubject)[subjectId];
        let detail;

        subSubject = getTextFromItemDimension(subSubjecsArray, subSubject);
        subject = getTextFromItemDimension(subjectsArray, subject);
        detail = getTextFromItemDimension(detailsArray, detailItem).replace(subSubject, '');

        subSubject = subSubject.replace(subject, '');
        subSubject = removeSeparator(subSubject);
        detail = removeSeparator(detail);

        return {
            "subject": subject,
            "subSubject": subSubject,
            "detail": detail
        };
    }

    const getSubSubjectItem = (subjectsArray, subSubjecsArray, subSubjectItem, subjectId) => {
        let subject = subSubjecsArray.find(x => x.id === subSubjectItem)[subjectId];
        let subSubject;

        subject = getTextFromItemDimension(subjectsArray, subject);
        subSubject = getTextFromItemDimension(subSubjecsArray, subSubjectItem).replace(subSubject, '');
        subSubject = subSubject.replace(subject, '');
        subSubject = removeSeparator(subSubject);

        return {
            "subject": subject,
            "subSubject": subSubject
        };
    }

    const getSubjectItem = (subjectsArray, subjectItem) => {
        let subject = getTextFromItemDimension(subjectsArray, subjectItem);

        return {
            "subject": subject
        };
    }

    const buildGridData = (dim_subjectsArray, dim_subSubjectsArray, dim_detailsArray, subjectsArray, subSubjectsArray, detailsArray, subjectId, subSubjectId) => {
        let gridData = [];

        if (isArrayNotEmpty(dim_detailsArray)) {
            dim_detailsArray.forEach(detailItem => {
                let item = getDetailItem(subjectsArray, subSubjectsArray, detailsArray, detailItem, subjectId, subSubjectId);
                gridData.push(item);
            });
        }

        if (isArrayNotEmpty(dim_subSubjectsArray)) {
            dim_subSubjectsArray.forEach(subSubjectItem => {
                let item = getSubSubjectItem(subjectsArray, subSubjectsArray, subSubjectItem, subjectId);
                if (!gridData.some(x => x.subSubject === item.subSubject)) {
                    gridData.push(item);
                }
            });
        }

        if (isArrayNotEmpty(dim_subjectsArray)) {
            dim_subjectsArray.forEach(subjectItem => {
                let item = getSubjectItem(subjectsArray, subjectItem);
                if (!gridData.some(x => x.subject === item.subject)) {
                    gridData.push(item);
                }
            });
        }

        return gridData;
    }

    //Build Syllabus data for the grid-table
    useEffect(() => {
        setSyllabusDataForGrid(buildGridData(
            itemDimensions.syllabusSubjects, itemDimensions.syllabusSubsubjects, itemDimensions.syllabusDetails,
            syllabusSubjectsArray, syllabusSubsubjectsArray, syllabusDetailsArray,
            SYLLABUS_SUBJECT_ID, SYLLABUS_SUBSUBJECT_ID));
    }, [itemDimensions.syllabusSubjects, itemDimensions.syllabusSubsubjects, itemDimensions.syllabusDetails]);

    //Build Skills data for the grid-table
    useEffect(() => {
        setSkillsDataForGrid(buildGridData(
            itemDimensions.skills, itemDimensions.subskills, itemDimensions.skillsDetails,
            skillsArray, subskillsArray, skillsDetailsArray,
            SKILLS_SUBJECT_ID, SKILLS_SUBSUBJECT_ID));
    }, [itemDimensions.skills, itemDimensions.subskills, itemDimensions.skillsDetails]);

    //Build General-Skills data for the grid-table
    useEffect(() => {
        setGeneralSkillsDataForGrid(buildGridData(
            itemDimensions.generalSkills, itemDimensions.generalSubskills, itemDimensions.generalSkillsDetails,
            generalSkillsArray, generalSubskillsArray, generalSkillsDetailsArray,
            SKILLS_SUBJECT_ID, SKILLS_SUBSUBJECT_ID));
    }, [itemDimensions.generalSkills, itemDimensions.generalSubskills, itemDimensions.generalSkillsDetails]);


    return (
        <div className="app-repository_item_editor">
            <UpdateController 
                lastEditor={item.lastUpdatedUser} 
                lastUpdate={formatDate(item.lastUpdatedDate)} 
                hasChanges={updateData.hasChanges} 
                update={updateItem} 
                topTools={topTools}
                sidebarContent={
                    <PublishWorkspacePanel
                        isPublished={item.isPublished}
                        isValidForm={isValidForm}
                        publishHint={getHintForPublish()}
                        onPublishChange={(value) => onFieldChange('isPublished', value)}
                        workspaces={workspaces}
                        selectedWorkspaces={itemDimensions.workspaces}
                        onWorkspacesChange={(newWorkspaces) => {
                            let new_itemDimensions = { ...itemDimensions };
                            new_itemDimensions.workspaces = newWorkspaces;
                            setItemDimensions(new_itemDimensions);
                            setUpdateData({ hasChanges: true });
                        }}
                        showSkillsError={!isBookOrCourse && !isPedagogyNonSkills && (isArrayEmpty(itemDimensions.skills) && isArrayEmpty(itemDimensions.generalSkills))}
                        skillsErrorMessage={itemDimensions.workspaces?.some(ws => ws.toLowerCase() === 'ivritil') ? '' : localization.labels.repositoryManagement.validators.requiredSkills}
                        publishLabel={localization.labels.repositoryManagement.item.isPublished}
                        workspaceLabel={localization.labels.repositoryManagement.item.workspace || 'מרחב עבודה'}
                    />
                }
            >
                <Form onSubmit={handleSubmit}
                    initialValues={item}
                    // validator={formValidator}
                    render={(formRenderProps) => (
                        <FormElement horizontal={true} style={{ width: '100%' }}>
                            <fieldset className={'k-form-fieldset' + (isBookOrCourse ? ' book_course' : '')} >
                                <Card id="section-info">
                                    <CardBody>
                                        <CardTitle>{localization.labels.repositoryManagement.item.id}: <strong> {item.id ? item.id : 'new item'}</strong></CardTitle>
                                        {/* 
                                    <Field
                                        disabled={noInsertMode}
                                        component={FormInput}
                                        id={'contextURL'}
                                        name={'contextURL'}
                                        label={localization.labels.repositoryManagement.item.contextURL}
                                        value={item.contextURL}
                                        onChange={(event) => {
                                            onFieldChange('contextURL', event.value);
                                        }}
                                    /> */}

                                        <div className="k-form-field">
                                            <label htmlFor="contextURL" className="k-label">{localization.labels.repositoryManagement.item.contextURL}</label>
                                            <a target="_blank"
                                                rel='noreferrer'
                                                id={'contextURL'}
                                                name={'contextURL'}
                                                href={item.contextURL}>
                                                {item.contextURL}
                                            </a>
                                        </div>

                                        <Field
                                            component={FormMultiSelect}
                                            id={'itemTypes'}
                                            name={'itemTypes'}
                                            data={itemTypes}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.itemTypes, dimensions.itemTypes)}
                                            label={localization.labels.repositoryManagement.item.itemTypes}
                                            hint={localization.labels.repositoryManagement.hints.itemTypes}
                                            //valid={isArrayNotEmpty(itemDimensions.itemTypes)}
                                            validationMessage={isArrayNotEmpty(itemDimensions.itemTypes) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('itemTypes', event.value, 2);
                                                if (event.value.find(type => type.id === 'course')) {
                                                    item.duration = 0;
                                                }
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card id="section-title">
                                    <CardBody>
                                        <Field
                                            component={FormInput}
                                            id={'title'}
                                            name={'title'}
                                            label={localization.labels.repositoryManagement.item.title}
                                            value={item.title}
                                            //valid={item.title}
                                            validationMessage={item.title ? '' : localization.labels.repositoryManagement.validators.required}
                                            //required={true}
                                            //validator={requiredValidator}                                                                                
                                            hint={`${localization.labels.repositoryManagement.hints.title} 
                                                ${item.title ? item.title.length : 0}/${TITLE_MAX_CHARS} 
                                                ${localization.labels.repositoryManagement.hints.chars}`}
                                            maxLength={TITLE_MAX_CHARS}
                                            autoComplete="off"
                                            onChange={(event) => {
                                                onFieldChange('title', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormInput}
                                            id={'subtitle'}
                                            name={'subtitle'}
                                            value={item.subtitle}
                                            label={localization.labels.repositoryManagement.item.subtitle}
                                            hint={`${localization.labels.repositoryManagement.hints.subtitle} 
                                                ${item.subtitle ? item.subtitle.length : 0}/${SUBTITLE_MAX_CHARS} 
                                                ${localization.labels.repositoryManagement.hints.chars}`}
                                            maxLength={SUBTITLE_MAX_CHARS}
                                            autoComplete="off"
                                            onChange={(event) => {
                                                onFieldChange('subtitle', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <div className="k-form-field">
                                            <label htmlFor="thumbnailId" className="k-label">{localization.labels.repositoryManagement.item.thumbnailId}</label>
                                            <Uploader type={uploaderTypes.thumbnail} thumbnailId={item.thumbnailId} style={{ flex: 1 }} onChange={(file) => {
                                                setNewThumbnail(file);
                                                setUpdateData({ hasChanges: true });
                                            }} />
                                        </div>

                                        <Field
                                            component={FormCheckbox}
                                            id={'designApproved'}
                                            name={'designApproved'}
                                            label={localization.labels.repositoryManagement.item.designApproved}
                                            value={item.designApproved}
                                            onChange={(event) => {
                                                onFieldChange('designApproved', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <Field
                                            component={FormTextArea}
                                            id={'summary'}
                                            name={'summary'}
                                            label={localization.labels.repositoryManagement.item.summary}
                                            hint={`${localization.labels.repositoryManagement.hints.summary} 
                                                 ${item.summary ? item.summary.length : 0}/${isBookOrCourse ? SUMMARY_MAX_CHARS_BOOK : SUMMARY_MAX_CHARS} 
                                                 ${localization.labels.repositoryManagement.hints.chars}`}
                                            value={item.summary && item.summary.replace(/<[^>]*>/g, '')}
                                            //valid={item.summary}
                                            validationMessage={item.summary ? '' : localization.labels.repositoryManagement.validators.required}
                                            defaultValue={''}
                                            maxLength={isBookOrCourse ? SUMMARY_MAX_CHARS_BOOK : SUMMARY_MAX_CHARS}
                                            rows={3}
                                            autoSize={true}
                                            onChange={(event) => {
                                                onFieldChange('summary', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormDropDownList}
                                            id={'activityType'}
                                            name={'activityType'}
                                            data={activityTypes}
                                            textField={DIMENSIONS_LANG}
                                            value={getActivityTypeValue(item.activityType, dimensions.activityTypes)}
                                            label={localization.labels.repositoryManagement.item.activityType}
                                            hint={isBookOrCourse ? '' : localization.labels.repositoryManagement.hints.activityType}
                                            validationMessage={isBookOrCourse ? '' : item.activityType ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onFieldChange('activityType', event.value.id);
                                            }}
                                        />

                                        {showSubActivityType && <Field
                                            component={FormDropDownList}
                                            id={'subActivityType'}
                                            name={'subActivityType'}
                                            data={subActivityTypes}
                                            textField={DIMENSIONS_LANG}
                                            value={getActivityTypeValue(item.subActivityType, dimensions.subActivityTypes)}
                                            label={localization.labels.repositoryManagement.item.subActivityType}
                                            // hint={!isPedagogy ? '' : localization.labels.repositoryManagement.hints.subActivityType}
                                            validationMessage={!isPedagogy ? '' : item.subActivityType ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onFieldChange('subActivityType', event.value.id);
                                            }}
                                        />}

                                        {isCourse ?
                                            <Field
                                                component={FormNumericTextBox}
                                                id={'duration'}
                                                name={'duration'}
                                                label={localization.labels.repositoryManagement.item.duration}
                                                value={item.duration}
                                                validationMessage={item.duration ? '' : localization.labels.repositoryManagement.validators.required}
                                                hint={localization.labels.repositoryManagement.hints.duration}
                                                min={1}
                                                max={999}
                                                maxLength={3}
                                                onChange={(event) => {
                                                    onFieldChange('duration', event.value);
                                                }}
                                            />
                                            :
                                            <Field
                                                component={FormDropDownList}
                                                disabled={isBookOrCourse}
                                                id={'duration'}
                                                name={'duration'}
                                                label={localization.labels.repositoryManagement.item.duration}
                                                data={durationData}
                                                textField={'displayValue'}
                                                value={getDurationValue(item.duration)}
                                                validationMessage={isBookOrCourse ? '' : item.duration ? '' : localization.labels.repositoryManagement.validators.required}
                                                onChange={(event) => {
                                                    onFieldChange('duration', event.value.value);
                                                }}
                                            />
                                        }

                                        <Field
                                            component={FormDropDownList}
                                            id={'audiences'}
                                            name={'audiences'}
                                            data={audiences}
                                            disabled={isPedagogy}
                                            textField={DIMENSIONS_LANG}
                                            label={localization.labels.repositoryManagement.item.audiences}
                                            value={getValueForDropDownList(itemDimensions.audiences, dimensions.audiences)}
                                            validationMessage={isArrayNotEmpty(itemDimensions.audiences) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onDropDownListFieldChange('audiences', event.value);
                                            }}
                                        />

                                        <div class="k-form-field audiences-hint">
                                            <div class="k-form-hint">
                                                <p>
                                                    {getAudiencesRow(0)}
                                                    {getAudiencesRow(1)}
                                                    {getAudiencesRow(2)}
                                                    {getAudiencesRow(3)}
                                                    {getAudiencesRow(4)}
                                                </p>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card id="section-disciplines">
                                    <CardBody>
                                        <CardTitle>{localization.labels.repositoryManagement.item.disciplines}</CardTitle>

                                        <RadioGroup
                                            id={'radioGroupDisciplines'}
                                            data={disciplinesChoice}
                                            disabled={!isPedagogy}
                                            value={getDisciplinesValue()}
                                            onChange={(event) => {
                                                onFieldChange('isAllDisciplines', event.value === 'allDisciplines');
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'disciplines'}
                                            name={'disciplines'}
                                            disabled={isPedagogy && item.isAllDisciplines}
                                            data={disciplines}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.disciplines, dimensions.disciplines)}
                                            label={localization.labels.repositoryManagement.item.disciplines}
                                            validationMessage={(isArrayNotEmpty(itemDimensions.disciplines) || item.isAllDisciplines) ? '' : localization.labels.repositoryManagement.validators.required}
                                            // maxSelectedItems={3}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('disciplines', event.value, DISCIPLINES_MAX);
                                                updateSectorsByDisciplines(event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <Field
                                            component={FormMultiSelect}
                                            id={'sectors'}
                                            name={'sectors'}
                                            data={sectors}
                                            autoClose={false}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(isPedagogy && item.isAllDisciplines ? itemDimensions.sectors : sectorsByDis, dimensions.sectors)}
                                            label={localization.labels.repositoryManagement.item.sectors}
                                            validationMessage={isArrayNotEmpty(itemDimensions.sectors) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                filterSectors(event.value);
                                                onMultiSelectFieldChange('sectors', event.value);
                                            }}
                                        />

                                        <Field
                                            disabled={NO_INSERT_MODE}
                                            component={FormDropDownList}
                                            id={'contextLanguage'}
                                            name={'contextLanguage'}
                                            //data={'he','ar','en'}
                                            //textField={dimensionsLang}
                                            label={localization.labels.repositoryManagement.item.contextLanguage}
                                            value={item.contextLanguage}
                                        // onChange={(event) => {
                                        //     onDropDownListFieldChange('contextLanguage', event.value);
                                        // }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'ageGrades'}
                                            name={'ageGrades'}
                                            data={ageGrades}
                                            autoClose={false}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.ageGrades, dimensions.ageGrades)}
                                            label={localization.labels.repositoryManagement.item.ageGrades}
                                            validationMessage={isArrayNotEmpty(itemDimensions.ageGrades) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('ageGrades', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <CardTitle>
                                            {localization.labels.repositoryManagement.item.yearCyclesCard}
                                        </CardTitle>

                                        <Field
                                            component={FormMultiSelect}
                                            id={'yearCycleCategories'}
                                            name={'yearCycleCategories'}
                                            data={yearCycleCategories}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.yearCycleCategories, dimensions.yearCycleCategories)}
                                            label={localization.labels.repositoryManagement.item.yearCycleCategories}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('yearCycleCategories', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'yearCycles'}
                                            name={'yearCycles'}
                                            data={yearCyclesFiltered}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.yearCycles, dimensions.yearCycles)}
                                            label={localization.labels.repositoryManagement.item.yearCycles}
                                            hint={localization.labels.repositoryManagement.hints.yearCycles}
                                            disabled={isArrayEmpty(itemDimensions.yearCycles) && isArrayEmpty(itemDimensions.yearCycleCategories)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('yearCycles', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card id="section-syllabus">
                                    <CardBody>
                                        <CardTitle>
                                            {localization.labels.repositoryManagement.item.syllabus}
                                            {isBookOrCourse ? '' : ` (${getMsgSyllabusLimit()})`}
                                        </CardTitle>

                                        <Field
                                            component={FormMultiSelect}
                                            id={'syllabusSubjects'}
                                            name={'syllabusSubjects'}
                                            data={syllabusSubjects}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.syllabusSubjects, dimensions.syllabusSubjects)}
                                            label={localization.labels.repositoryManagement.item.subjects}
                                            validationMessage={getValidationMessageForSyllabusSubjects()}
                                            disabled={isArrayEmpty(itemDimensions.ageGrades) || isArrayEmpty(itemDimensions.disciplines)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('syllabusSubjects', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSyllabusSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'syllabusSubsubjects'}
                                            name={'syllabusSubsubjects'}
                                            data={syllabusSubsubjects}
                                            textField={DIMENSIONS_LANG}
                                            disabled={isArrayEmpty(itemDimensions.syllabusSubjects)}
                                            value={getValueForMultiSelect(itemDimensions.syllabusSubsubjects, dimensions.syllabusSubsubjects)}
                                            label={localization.labels.repositoryManagement.item.subSubjects}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('syllabusSubsubjects', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSyllabusSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'syllabusDetails'}
                                            name={'syllabusDetails'}
                                            data={syllabusDetails}
                                            textField={DIMENSIONS_LANG}
                                            disabled={isArrayEmpty(itemDimensions.syllabusSubsubjects)}
                                            value={getValueForMultiSelect(itemDimensions.syllabusDetails, dimensions.syllabusDetails)}
                                            label={localization.labels.repositoryManagement.item.details}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('syllabusDetails', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSyllabusSubjects);
                                            }}
                                        />

                                        <Grid className="grid-syllabus"
                                            data={syllabusDataForGrid}>
                                            <Column field="subject" title={localization.labels.repositoryManagement.item.subjects} />
                                            <Column field="subSubject" title={localization.labels.repositoryManagement.item.subSubjects} />
                                            <Column field="detail" title={localization.labels.repositoryManagement.item.details} />
                                        </Grid>

                                        <footer className="grid-footer">
                                            <div className="total-items">
                                                {localization.labels.repositoryManagement.item.selected}
                                                &nbsp;<strong>{syllabusDataForGrid.length}</strong>
                                                &nbsp;{localization.labels.repositoryManagement.item.syllabusLimit}
                                            </div>
                                            <div className="validation-message">
                                                {getValidationMessageForSyllabus(syllabusDataForGrid.length, maxSyllabusSubjects, getMsgSyllabusLimit())}
                                            </div>
                                        </footer>
                                    </CardBody>
                                </Card>

                                <Card id="section-skills">
                                    <CardBody>
                                        <CardTitle>
                                            {localization.labels.repositoryManagement.item.professional}
                                            {isBookOrCourse ? '' : ` (${getMsgSkillsLimit()})`}
                                        </CardTitle>
                                        <Field
                                            component={FormMultiSelect}
                                            id={'skills'}
                                            name={'skills'}
                                            data={skills}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.skills, dimensions.skills)}
                                            label={localization.labels.repositoryManagement.item.subjects}
                                            disabled={isArrayEmpty(itemDimensions.ageGrades) || isArrayEmpty(itemDimensions.disciplines)}
                                            //  validationMessage={(isBookOrCourse || isArrayNotEmpty(itemDimensions.skills)) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('skills', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSkillsSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'subskills'}
                                            name={'subskills'}
                                            data={subskills}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.subskills, dimensions.subskills)}
                                            label={localization.labels.repositoryManagement.item.subSubjects}
                                            disabled={isArrayEmpty(itemDimensions.skills)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('subskills', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSkillsSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'skillsDetails'}
                                            name={'skillsDetails'}
                                            data={skillsDetails}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.skillsDetails, dimensions.skillsDetails)}
                                            label={localization.labels.repositoryManagement.item.details}
                                            disabled={isArrayEmpty(itemDimensions.subskills)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('skillsDetails', event.value, isAllowMaxForBookOrCourse(event) ? null : maxSkillsSubjects);
                                            }}
                                        />

                                        <Grid className="grid-skill"
                                            data={skillsDataForGrid}>
                                            <Column field="subject" title={localization.labels.repositoryManagement.item.subjects} />
                                            <Column field="subSubject" title={localization.labels.repositoryManagement.item.subSubjects} />
                                            <Column field="detail" title={localization.labels.repositoryManagement.item.details} />
                                        </Grid>

                                        <footer className="grid-footer">
                                            <div className="total-items">
                                                {localization.labels.repositoryManagement.item.selected}
                                                &nbsp;<strong>{skillsDataForGrid.length}</strong>
                                                &nbsp;{localization.labels.repositoryManagement.item.skillsLimit}
                                            </div>
                                            <div className="validation-message">
                                                {getValidationMessageForSkills(skillsDataForGrid.length, maxSkillsSubjects, getMsgSkillsLimit())}
                                            </div>
                                        </footer>
                                    </CardBody>
                                </Card>

                                <Card id="section-general-skills">
                                    <CardBody>
                                        <CardTitle>
                                            {localization.labels.repositoryManagement.item.general}
                                            {isBookOrCourse ? '' : ` (${getMsgSkillsLimit(true)})`}
                                        </CardTitle>
                                        <Field
                                            component={FormMultiSelect}
                                            id={'generalSkills'}
                                            name={'generalSkills'}
                                            data={generalSkills}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.generalSkills, dimensions.generalSkills)}
                                            label={localization.labels.repositoryManagement.item.subjects}
                                            disabled={isArrayEmpty(itemDimensions.ageGrades)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('generalSkills', event.value, isAllowMaxForBookOrCourse(event) ? null : maxGeneralSkillsSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'generalSubskills'}
                                            name={'generalSubskills'}
                                            data={generalSubskills}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.generalSubskills, dimensions.generalSubskills)}
                                            label={localization.labels.repositoryManagement.item.subSubjects}
                                            disabled={isArrayEmpty(itemDimensions.generalSkills)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('generalSubskills', event.value, isAllowMaxForBookOrCourse(event) ? null : maxGeneralSkillsSubjects);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'generalSkillsDetails'}
                                            name={'generalSkillsDetails'}
                                            data={generalSkillsDetails}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.generalSkillsDetails, dimensions.generalSkillsDetails)}
                                            label={localization.labels.repositoryManagement.item.details}
                                            disabled={isArrayEmpty(itemDimensions.generalSubskills)}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('generalSkillsDetails', event.value, isAllowMaxForBookOrCourse(event) ? null : maxGeneralSkillsSubjects);
                                            }}
                                        />

                                        <Grid className="grid-skill"
                                            data={generalSkillsDataForGrid}>
                                            <Column field="subject" title={localization.labels.repositoryManagement.item.subjects} />
                                            <Column field="subSubject" title={localization.labels.repositoryManagement.item.subSubjects} />
                                            <Column field="detail" title={localization.labels.repositoryManagement.item.details} />
                                        </Grid>

                                        <footer className="grid-footer">
                                            <div className="total-items">
                                                {localization.labels.repositoryManagement.item.selected}
                                                &nbsp;<strong>{generalSkillsDataForGrid.length}</strong>
                                                &nbsp;{localization.labels.repositoryManagement.item.skillsLimit}
                                            </div>
                                            <div className="validation-message">
                                                {getValidationMessageForSkills(generalSkillsDataForGrid.length, maxSkillsSubjects, getMsgSkillsLimit(true))}
                                            </div>
                                        </footer>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <Field
                                            component={FormMultiSelect}
                                            id={'pedagogicValues'}
                                            name={'pedagogicValues'}
                                            data={pedagogicValues}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.pedagogicValues, dimensions.pedagogicValues)}
                                            label={localization.labels.repositoryManagement.item.pedagogicValues}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('pedagogicValues', event.value, 2);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <Field
                                            component={FormMultiSelect}
                                            id={'authors'}
                                            name={'authors'}
                                            data={authors}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.authors, dimensions.authors)}
                                            label={localization.labels.repositoryManagement.item.authors}
                                            // validationMessage={isArrayNotEmpty(itemDimensions.authors) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('authors', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'publishers'}
                                            name={'publishers'}
                                            data={publishers}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.publishers, dimensions.publishers)}
                                            label={localization.labels.repositoryManagement.item.publishers}
                                            // validationMessage={isArrayNotEmpty(itemDimensions.publishers) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('publishers', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormMultiSelect}
                                            id={'copyrightHolders'}
                                            name={'copyrightHolders'}
                                            data={copyrightHolders}
                                            textField={DIMENSIONS_LANG}
                                            value={getValueForMultiSelect(itemDimensions.copyrightHolders, dimensions.copyrightHolders)}
                                            label={localization.labels.repositoryManagement.item.copyrightHolders}
                                            // validationMessage={isArrayNotEmpty(itemDimensions.copyrightHolders) ? '' : localization.labels.repositoryManagement.validators.required}
                                            onChange={(event) => {
                                                onMultiSelectFieldChange('copyrightHolders', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card id="section-internal">
                                    <CardBody>
                                        <Field
                                            component={FormCheckbox}
                                            disabled={true}
                                            id={'isMobileSupported'}
                                            name={'isMobileSupported'}
                                            label={localization.labels.repositoryManagement.item.isMobileSupported}
                                            value={item.isMobileSupported}
                                            onChange={(event) => {
                                                onFieldChange('isMobileSupported', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormCheckbox}
                                            disabled={true}
                                            id={'isNeedsPayment'}
                                            name={'isNeedsPayment'}
                                            label={localization.labels.repositoryManagement.item.isNeedsPayment}
                                            value={item.isNeedsPayment}
                                            onChange={(event) => {
                                                onFieldChange('isNeedsPayment', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormCheckbox}
                                            id={'isMobileAdjustmentRequired'}
                                            name={'isMobileAdjustmentRequired'}
                                            label={localization.labels.repositoryManagement.item.isMobileAdjustmentRequired}
                                            value={item.isMobileAdjustmentRequired}
                                            onChange={(event) => {
                                                onFieldChange('isMobileAdjustmentRequired', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormCheckbox}
                                            id={'isImprovementRequired'}
                                            name={'isImprovementRequired'}
                                            label={localization.labels.repositoryManagement.item.isImprovementRequired}
                                            value={item.isImprovementRequired}
                                            onChange={(event) => {
                                                onFieldChange('isImprovementRequired', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormCheckbox}
                                            id={'arTranslationRequired'}
                                            name={'arTranslationRequired'}
                                            label={localization.labels.repositoryManagement.item.arTranslationRequired}
                                            value={item.arTranslationRequired}
                                            onChange={(event) => {
                                                onFieldChange('arTranslationRequired', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormCheckbox}
                                            id={'doNotImport'}
                                            name={'doNotImport'}
                                            label={localization.labels.repositoryManagement.item.doNotImport}
                                            value={item.doNotImport}
                                            onChange={(event) => {
                                                onFieldChange('doNotImport', event.value);
                                            }}
                                        />

                                        <Field
                                            component={FormTextArea}
                                            id={'internalComments'}
                                            name={'internalComments'}
                                            label={localization.labels.repositoryManagement.item.internalComments}
                                            value={item.internalComments}
                                            rows={3}
                                            autoSize={true}
                                            onChange={(event) => {
                                                onFieldChange('internalComments', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                <Card id="section-publish">
                                    <CardBody>
                                        <Field
                                            component={FormCheckbox}
                                            id={'isInformaticApproved'}
                                            name={'isInformaticApproved'}
                                            label={localization.labels.repositoryManagement.item.isInformaticApproved}
                                            value={item.isInformaticApproved}
                                            onChange={(event) => {
                                                onInformaticApprovedFieldChange('isInformaticApproved', event.value);
                                            }}
                                        />
                                    </CardBody>
                                </Card>

                                {isCourse && <details id="marketing">
                                    <summary>{localization.labels.repositoryManagement.marketing.title}</summary>
                                    <Card>
                                        <CardBody>
                                            <CardHeader>
                                                <CardTitle>{localization.labels.repositoryManagement.marketing.subTitle}</CardTitle>
                                            </CardHeader>
                                            <Field
                                                component={FormInput}
                                                id={'courseMarketingURL'}
                                                name={'courseMarketingURL'}
                                                label={localization.labels.repositoryManagement.marketing.courseMarketingURL}
                                                value={getValueFromJson(item.additionalInfo, 'courseData', 'courseMarketingURL')}
                                                validationMessage={isValidUrl(getValueFromJson(item.additionalInfo, 'courseData', 'courseMarketingURL')) ? '' : localization.labels.repositoryManagement.validators.invalidUrl}
                                                maxLength={URL_MAX_CHARS}
                                                autoComplete="off"
                                                onChange={(event) => {
                                                    onAdditionalInfoFieldChange('courseData', 'courseMarketingURL', event.value);
                                                }}
                                            />

                                            <Field
                                                component={FormInput}
                                                id={'courseVideoURL'}
                                                name={'courseVideoURL'}
                                                label={localization.labels.repositoryManagement.marketing.courseVideoURL}
                                                value={getValueFromJson(item.additionalInfo, 'courseData', 'courseVideoURL')}
                                                validationMessage={isValidUrl(getValueFromJson(item.additionalInfo, 'courseData', 'courseVideoURL')) ? '' : localization.labels.repositoryManagement.validators.invalidUrl}
                                                maxLength={URL_MAX_CHARS}
                                                autoComplete="off"
                                                onChange={(event) => {
                                                    onAdditionalInfoFieldChange('courseData', 'courseVideoURL', event.value);
                                                }}
                                            />
                                            <VideoPreview videoUrl={getValueFromJson(item.additionalInfo, 'courseData', 'courseVideoURL')} />

                                            <Field
                                                component={FormTextArea}
                                                id={'courseContent'}
                                                name={'courseContent'}
                                                label={localization.labels.repositoryManagement.marketing.courseContent}
                                                value={getValueFromJson(item.additionalInfo, 'courseData', 'courseContent')}
                                                hint={localization.labels.repositoryManagement.hints.courseContent}
                                                validationMessage={isValidMaxRows(COURSE_CONTENT_MAX_ROWS, getValueFromJson(item.additionalInfo, 'courseData', 'courseContent')) ? '' : localization.labels.repositoryManagement.validators.invalidCourseContentMaxRows}
                                                defaultValue={''}
                                                maxLength={COURSE_CONTENT_MAX_CHARS}
                                                rows={COURSE_CONTENT_MAX_ROWS}
                                                onChange={(event) => {
                                                    onAdditionalInfoFieldChange('courseData', 'courseContent', event.value);
                                                }}
                                            />

                                            <Field
                                                component={FormCheckbox}
                                                id={'courseShowCreateEnvironmentButton'}
                                                name={'courseShowCreateEnvironmentButton'}
                                                label={localization.labels.repositoryManagement.marketing.courseShowCreateEnvironmentButton}
                                                value={getValueFromJson(item.additionalInfo, 'courseData', 'courseShowCreateEnvironmentButton')}
                                                onChange={(event) => {
                                                    onAdditionalInfoFieldChange('courseData', 'courseShowCreateEnvironmentButton', event.value);
                                                }}
                                            />

                                            {/* //     additionalInfo.courseData = {
                                            //          courseVideoURL: "https://demoVideo.com",
                                            //          courseMarketingURL: "https://demoMarketing.com",
                                            //          courseContent: 'alfa;beta',
                                            //          courseShowCreateEnvironmentButton: true
                                            //     }; */}

                                        </CardBody>
                                    </Card>
                                </details>
                                }

                                {isPedagogy && <details id="nano">
                                    <summary>{localization.labels.repositoryManagement.nano.title}</summary>
                                    <Card>
                                        <CardBody>
                                            <CardHeader>
                                                <CardTitle>{localization.labels.repositoryManagement.nano.subTitle}</CardTitle>
                                            </CardHeader>

                                            <Field
                                                component={FormTextArea}
                                                id={'nanoContent'}
                                                name={'nanoContent'}
                                                label={localization.labels.repositoryManagement.nano.nanoContent}
                                                value={getValueFromJson(item.additionalInfo, 'nanoData', 'nanoContent')}
                                                hint={localization.labels.repositoryManagement.hints.nanoContent}
                                                validationMessage={isValidMaxRows(COURSE_CONTENT_MAX_ROWS, getValueFromJson(item.additionalInfo, 'nanoData', 'nanoContent')) ? '' : localization.labels.repositoryManagement.validators.invalidCourseContentMaxRows}
                                                defaultValue={''}
                                                maxLength={COURSE_CONTENT_MAX_CHARS}
                                                rows={COURSE_CONTENT_MAX_ROWS}
                                                onChange={(event) => {
                                                    onAdditionalInfoFieldChange('nanoData', 'nanoContent', event.value);
                                                }}
                                            />
                                        </CardBody>
                                    </Card>
                                </details>
                                }

                                <Card className="section-info">
                                    <CardBody>
                                        {item.otherLangItemId &&
                                            <div className="other-lang-Item">
                                                <div>
                                                    <label>{localization.labels.repositoryManagement.item.otherLangItemId}</label>
                                                    <label>{item.contextLanguage === 'he' ?
                                                        localization.labels.repositoryManagement.item.otherLangAr
                                                        : localization.labels.repositoryManagement.item.otherLangHe}
                                                    </label>
                                                </div>
                                                <a target="_blank" rel="noreferrer"
                                                    href={`${window.location.origin}/repository/all/${item.otherLangItemId}`}>
                                                    {item.otherLangItemId}</a>
                                            </div>
                                        }

                                        <Field
                                            disabled={true}
                                            component={FormInput}
                                            id={'itemContext'}
                                            name={'itemContext'}
                                            label={localization.labels.repositoryManagement.item.itemContext}
                                            value={getTextFromItemDimension(itemContext, item.itemContext)}
                                            onChange={(event) => {
                                                onFieldChange('itemContext', event.value);
                                            }}
                                        />

                                        <Field
                                            disabled={NO_INSERT_MODE}
                                            component={FormInput}
                                            id={'itemSource'}
                                            name={'itemSource'}
                                            label={localization.labels.repositoryManagement.item.itemSource}
                                            value={getTextFromItemDimension(itemSource, item.itemSource)}
                                            onChange={(event) => {
                                                onFieldChange('itemSource', event.value);
                                            }}
                                        />

                                        <Field
                                            disabled={NO_INSERT_MODE}
                                            component={FormInput}
                                            id={'externalContextId'}
                                            name={'externalContextId'}
                                            label={localization.labels.repositoryManagement.item.externalContextId}
                                            value={item.externalContextId}
                                            onChange={(event) => {
                                                onFieldChange('externalContextId', event.value);
                                            }}
                                        />

                                        <ul id="dates-info">
                                            <li><strong>{localization.labels.repositoryManagement.lastUpdate}:</strong> {formatDate(item.lastUpdatedDate)} {localization.labels.repositoryManagement.by} {item.lastUpdatedUser}</li>
                                            <li><strong>{localization.labels.repositoryManagement.lastPublishedCatalog}:</strong> {formatDate(item.lastCatalogPublishedDate)}</li>
                                            <li><strong>{localization.labels.repositoryManagement.added}:</strong> {formatDate(item.addedDate)} {localization.labels.repositoryManagement.by} {item.addedUser}</li>
                                            <li><strong>{localization.labels.repositoryManagement.lastPublished}:</strong> {formatDate(item.lastPublishedDate)} </li>
                                        </ul>
                                    </CardBody>
                                </Card>
                            </fieldset>
                        </FormElement>
                    )}
                />
            </UpdateController>
        </div>
    );
};